// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get } from './api';
import { ENDPOINTS } from '../utils/constants';
import { getStoreObject, patchStoreObject } from './objectStoreService';
import type { PlanFile } from '../types';

export interface Plan {
  storeKey: string;
  date: string;
  dc_site_id: string;
  status: 'review-required' | 'approved' | 'denied';
  reviewer_id?: string;
  reviewer_name?: string;
  review_date?: string;
  attributes: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

function enumerateDates(fromDate: string, toDate: string): string[] {
  const dates: string[] = [];
  const [fy, fm, fd] = fromDate.split('-').map(Number);
  const current = Date.UTC(fy, fm - 1, fd);
  const [ty, tm, td] = toDate.split('-').map(Number);
  const end = Date.UTC(ty, tm - 1, td);
  const MS_PER_DAY = 86_400_000;
  for (let ts = current; ts <= end; ts += MS_PER_DAY) {
    const d = new Date(ts);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${day}`);
  }
  return dates;
}

function responseToPlan(
  key: string,
  date: string,
  dcSiteId: string,
  data: {
    key: string;
    attributes?: Record<string, string>;
    createdAt?: string;
    updatedAt?: string;
  }
): Plan {
  const attrs = data.attributes ?? {};
  return {
    storeKey: key,
    date,
    dc_site_id: dcSiteId,
    status: (['review-required', 'approved', 'denied'] as const).includes(
      attrs.status as Plan['status']
    )
      ? (attrs.status as Plan['status'])
      : 'review-required',
    reviewer_id: attrs.reviewer_id || undefined,
    reviewer_name: attrs.reviewer_name || undefined,
    review_date: attrs.review_date || undefined,
    attributes: attrs,
    createdAt: data.createdAt ?? '',
    updatedAt: data.updatedAt ?? '',
  };
}

/**
 * Fetch plan metadata for every date in [fromDate, toDate].
 * Dates with no plan (404) are silently skipped.
 * Optionally filter by status client-side.
 */
export const getPlansForDateRange = async (
  dcSiteId: string,
  fromDate: string,
  toDate: string,
  status?: string
): Promise<Plan[]> => {
  const dates = enumerateDates(fromDate, toDate);
  const results = await Promise.allSettled(
    dates.map(date => getStoreObject(`plan_narration/${dcSiteId}/${date}.json`))
  );

  const plans: Plan[] = [];
  const errors: Error[] = [];
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (result.status === 'rejected') {
      const msg = result.reason?.message ?? '';
      // 404 means no plan for that date — skip silently.
      // The API layer returns "Object not found" (from backend detail) or "HTTP Error 404".
      if (msg.includes('404') || msg.includes('not found')) continue;
      errors.push(result.reason instanceof Error ? result.reason : new Error(msg));
      continue;
    }
    const { data } = result.value;
    if (!data) continue;
    const storeKey = `plan_narration/${dcSiteId}/${dates[i]}.json`;
    plans.push(responseToPlan(storeKey, dates[i], dcSiteId, data));
  }

  // If every request failed with a non-404 error, surface it
  if (plans.length === 0 && errors.length > 0) {
    throw errors[0];
  }

  const filtered = status && status !== 'all' ? plans.filter(p => p.status === status) : plans;
  return filtered.sort((a, b) => b.date.localeCompare(a.date));
};

/**
 * Fetch plan metadata.
 */
export const getPlan = async (storeKey: string): Promise<Plan> => {
  const { data } = await getStoreObject(storeKey);

  // Key format: plan_narration/{dcSiteId}/{date}.json
  const parts = storeKey.replace(/\.json$/, '').split('/');
  const dcSiteId = parts[1] ?? '';
  const date = parts[2] ?? '';

  return responseToPlan(storeKey, date, dcSiteId, data);
};

/**
 * Approve or deny a plan by PATCHing object store attributes.
 */
export const updatePlanStatus = async (
  storeKey: string,
  status: 'approved' | 'denied',
  reviewer: { id: string; name: string }
): Promise<void> => {
  await patchStoreObject(storeKey, {
    status,
    reviewer_id: reviewer.id,
    reviewer_name: reviewer.name,
    review_date: new Date().toISOString(),
  });
};

/**
 * Download and parse the plan JSON file via the BFF proxy.
 */
export const fetchPlanFile = async (storeKey: string): Promise<PlanFile> => {
  return get<PlanFile>(ENDPOINTS.OBJECT_STORE_DOWNLOAD(storeKey));
};
